// f64/viewport.wgsl — pixelToComplexDD, the double-double counterpart to
// helpers/viewport.wgsl's pixelToComplex. Needed because at deep zoom
// `(px - half_w) * scale` is itself the computation that first loses
// precision in f32 — two adjacent pixels start mapping to the same
// complex-plane point long before the escape-time iteration would.
//
// Requires, concatenated first: f64/dekker.wgsl, f64/utils/add.wgsl,
// f64/utils/multiply.wgsl, f64/utils/subtract.wgsl, and f64/complex.wgsl
// (ComplexDD) — see src/util/pipeline.mjs's F64_MODULES.

fn pixelToComplexDD(
  px: u32, py: u32,
  width: u32, height: u32,
  center_re: DD, center_im: DD,
  scale: DD,
  threadSlot: u32,
) -> ComplexDD {
  let half_w = f32(width) * 0.5;
  let half_h = f32(height) * 0.5;

  // Exact upcasts: pixel offsets are small integers, well within f32's
  // 24-bit exact-integer range, so lo is genuinely 0 here — no precision to
  // capture yet. scale is where the real magnitude range lives, which is
  // why it's the DD operand these get multiplied against.
  let dx = DD(f32(px) - half_w, 0.0);
  let dy = DD(f32(py) - half_h, 0.0);

  return ComplexDD(
    ddAddProtected(center_re, ddMulProtected(dx, scale, threadSlot), threadSlot),
    ddSubProtected(center_im, ddMulProtected(dy, scale, threadSlot), threadSlot),
  );
}
